"use client";

import RichTextWrapper from "@/components/atoms/RichTextWrapper";
import { generateLinkFromGeneralLink, generateTargetFromLink } from "@/lib/utils";
import { isRichTextNonEmptyWithImage } from "@/utils";
import { ComponentJapanSocialNetworks, UploadFile } from "@/types/generated";
import { useLocale } from "next-intl";
import Link from "next/link";

interface WanWanFooterProps {
  address?: string;
  check_in?: string;
  check_out?: string;
  social_links?: Partial<ComponentJapanSocialNetworks>;
  mobile?: string;
  hotel_logo?: Partial<UploadFile>;
  footer_description?: string | null;
  background_image?: Partial<UploadFile>;
  hotel_name?: string;
  timings?: string;
  theme?: string;
}

const WanWanFooter = ({
  address,
  check_in,
  check_out,
  social_links,
  mobile,
  hotel_logo,
  footer_description,
  background_image,
  hotel_name,
  timings,
  theme="forest"
}: WanWanFooterProps) => {
  const locale = useLocale();
  const logoUrl = hotel_logo?.cloudflare_url || hotel_logo?.url || undefined;
  const backgroundImageUrl = background_image?.cloudflare_url || background_image?.url || undefined;

  return (
    <div data-theme={theme} className="relative overflow-hidden py-[50px]">
      <div className="absolute inset-0 -z-10">
        {backgroundImageUrl && (
          <img
            src={backgroundImageUrl}
            alt="Footer Background"
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="absolute inset-0 -z-10 bg-white/70 " />

      {isRichTextNonEmptyWithImage(footer_description) && (
        <div >
          <RichTextWrapper content={footer_description as string} />
        </div>
      )}

      <div className="px-4 text-center">
        {/* Logo */}
        {logoUrl && (
          <div className="flex justify-center">
            <img src={logoUrl} alt="Hotel Logo" className="h-auto w-full max-w-50 lg:max-w-62.5" />
          </div>
        )}

        {/* Address & contact info */}
        <div className="mx-auto max-w-xl space-y-1.5 text-base lg:text-lg font-[700] text-[#203E5F] mt-5">
          {hotel_name && <p>{hotel_name}</p>}
          {address && <p>{address}</p>}
          {mobile && <p>{mobile} {timings}</p>}

          {check_in && (
            <p>チェックイン: {check_in}</p>
          )}
          {check_out && (
            <p>チェックアウト: {check_out}</p>
          )}
        </div>

        {/* Social icons */}
        <div className="mt-10 lg:mt-15 flex justify-center items-center gap-5">
          {generateLinkFromGeneralLink(social_links?.instagram, locale) !== "#" && (
            <Link
              target={generateTargetFromLink(social_links?.instagram)}
              href={generateLinkFromGeneralLink(social_links?.instagram, locale)}
            >
              <img className="size-7.5" src="/instagram.svg" alt="Instagram" />
            </Link>
          )}
          {generateLinkFromGeneralLink(social_links?.twitter, locale) !== "#" && (
            <Link
              target={generateTargetFromLink(social_links?.twitter)}
              href={generateLinkFromGeneralLink(social_links?.twitter, locale)}
            >
              <img className="size-7.5" src="/twitter.svg" alt="twitter" />
            </Link>
          )}
          {generateLinkFromGeneralLink(social_links?.facebook, locale) !== "#" && (
            <Link
              target={generateTargetFromLink(social_links?.facebook)}
              href={generateLinkFromGeneralLink(social_links?.facebook, locale)}
            >
              <img className="size-7.5" src="/facebook.svg" alt="Facebook" />
            </Link>
          )}
          {generateLinkFromGeneralLink(social_links?.youtube, locale) !== "#" && (
            <Link
              target={generateTargetFromLink(social_links?.youtube)}
              href={generateLinkFromGeneralLink(social_links?.youtube, locale)}
            >
              <img className="size-7.5" src="/youtube.svg" alt="YouTube" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default WanWanFooter;
